import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { PageData } from '../../types';
import { formatNumber, formatResponseTime } from '../../constants/nginx-sites';

interface RequestsTableProps {
  pages: PageData[] | null;
  apiEndpoints: PageData[] | null;
  color: string;
}

type TabValue = 'pages' | 'api';

export function RequestsTable({ pages, apiEndpoints, color }: RequestsTableProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('pages');
  const [showAll, setShowAll] = useState(false);

  const data = activeTab === 'pages' ? (pages || []) : (apiEndpoints || []);
  const displayData = showAll ? data : data.slice(0, 15);
  const maxCount = data.length > 0 ? Math.max(...data.map((d) => d.count)) : 1;
  const hasResponseTime = data.some(
    (d) => d.avgResponseTime !== undefined && d.avgResponseTime !== null
  );

  if ((!pages || pages.length === 0) && (!apiEndpoints || apiEndpoints.length === 0)) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Requisições</CardTitle>
          <div className="flex gap-1">
            {pages && pages.length > 0 && (
              <Button
                variant={activeTab === 'pages' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setActiveTab('pages');
                  setShowAll(false);
                }}
              >
                Páginas ({pages.length})
              </Button>
            )}
            {apiEndpoints && apiEndpoints.length > 0 && (
              <Button
                variant={activeTab === 'api' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  setActiveTab('api');
                  setShowAll(false);
                }}
              >
                API ({apiEndpoints.length})
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Caminho</TableHead>
              <TableHead className="text-right">Visitas</TableHead>
              {data.some((d) => d.unique !== undefined) && (
                <TableHead className="text-right">Únicos</TableHead>
              )}
              {hasResponseTime && (
                <TableHead className="text-right">Tempo Médio</TableHead>
              )}
              <TableHead className="w-[120px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayData.map((page) => (
              <TableRow key={page.path}>
                <TableCell className="font-mono text-xs max-w-[300px] truncate">
                  {page.path}
                </TableCell>
                <TableCell className="text-right">{formatNumber(page.count)}</TableCell>
                {page.unique !== undefined && (
                  <TableCell className="text-right">{formatNumber(page.unique)}</TableCell>
                )}
                {hasResponseTime && (
                  <TableCell className="text-right text-sm">
                    {page.avgResponseTime != null ? formatResponseTime(page.avgResponseTime) : '-'}
                  </TableCell>
                )}
                <TableCell>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${(page.count / maxCount) * 100}%`,
                        backgroundColor: color,
                      }}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.length > 15 && (
          <div className="mt-4 text-center">
            <Button variant="outline" size="sm" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Mostrar menos' : `Mostrar todos (${data.length})`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
